import React, { useState, useRef, useCallback } from 'react';
import { auth, database, storage } from '../../firebaseConfig';
import { ref as dbRef, push, set } from 'firebase/database';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './MultiVideoUploader.css';

let itemIdCounter = 0;

const SUPPORTED_FORMATS = [
    'video/mp4', 'video/webm', 'video/ogg',
    'video/avi', 'video/mov', 'video/wmv',
    'video/quicktime'
];
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;

function createVideoItem(file) {
    return {
        id: `vid_${Date.now()}_${itemIdCounter++}`,
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'pending',
        uploadProgress: 0,
        downloadURL: null,
        storagePath: null,
        fileMetadata: null,
        formData: {
            title: file.name.replace(/\.[^/.]+$/, ''),
            description: '',
            categoryId: ''
        },
        thumbnailFile: null,
        thumbnailPreview: null,
        error: null
    };
}

function optimizeFileName(fileName) {
    return fileName
        .toLowerCase()
        .replace(/[^a-z0-9.-]/g, '_')
        .replace(/_{2,}/g, '_');
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function MultiVideoUploader({ categories, onVideoAdded, onCancel }) {
    const [videoItems, setVideoItems] = useState([]);
    const fileInputRef = useRef(null);
    const videoRefs = useRef({});
    const uploadTasksRef = useRef({});
    const retryCountRef = useRef({});

    const MAX_AUTO_RETRIES = 3;

    const updateItem = useCallback((id, updates) => {
        setVideoItems(prev => prev.map(item =>
            item.id === id ? { ...item, ...updates } : item
        ));
    }, []);

    const attachUploadListeners = useCallback((uploadTask, item, storagePath, optimizedName) => {
        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                updateItem(item.id, { uploadProgress: Math.round(progress) });
            },
            (error) => {
                if (error.code === 'storage/canceled') {
                    updateItem(item.id, { status: 'error', error: 'Upload abgebrochen.' });
                    return;
                }

                const retries = retryCountRef.current[item.id] || 0;
                if (retries < MAX_AUTO_RETRIES) {
                    retryCountRef.current[item.id] = retries + 1;
                    console.log(`Upload retry ${retries + 1}/${MAX_AUTO_RETRIES} for ${item.file.name}`);
                    updateItem(item.id, {
                        status: 'uploading',
                        error: `Retry ${retries + 1}/${MAX_AUTO_RETRIES}...`
                    });

                    setTimeout(() => {
                        const storageRef = ref(storage, storagePath);
                        const newTask = uploadBytesResumable(storageRef, item.file, {
                            contentType: item.file.type,
                            customMetadata: {
                                originalName: item.file.name,
                                fileSize: item.file.size.toString(),
                                uploadedBy: auth.currentUser.uid,
                                uploadedAt: new Date().toISOString()
                            }
                        });
                        uploadTasksRef.current[item.id] = newTask;
                        attachUploadListeners(newTask, item, storagePath, optimizedName);
                    }, 2000 * (retries + 1));
                } else {
                    let errorMessage = 'Upload fehlgeschlagen nach mehreren Versuchen.';
                    switch (error.code) {
                        case 'storage/unauthorized': errorMessage = 'Keine Berechtigung.'; break;
                        case 'storage/quota-exceeded': errorMessage = 'Speicher voll.'; break;
                        default: errorMessage = `Fehler: ${error.message}`;
                    }
                    updateItem(item.id, { status: 'error', error: errorMessage });
                }
            },
            async () => {
                try {
                    retryCountRef.current[item.id] = 0;
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const videoEl = videoRefs.current[item.id];
                    delete uploadTasksRef.current[item.id];
                    updateItem(item.id, {
                        status: 'uploaded',
                        uploadProgress: 100,
                        downloadURL,
                        storagePath,
                        error: null,
                        fileMetadata: {
                            fileName: optimizedName,
                            originalName: item.file.name,
                            fileSize: item.file.size,
                            fileType: item.file.type,
                            duration: videoEl?.duration || 0,
                            width: videoEl?.videoWidth || 0,
                            height: videoEl?.videoHeight || 0
                        }
                    });
                } catch (err) {
                    updateItem(item.id, { status: 'error', error: 'Download-URL konnte nicht abgerufen werden.' });
                }
            }
        );
    }, [updateItem]);

    const startUpload = useCallback((item, existingStoragePath) => {
        if (!auth.currentUser) return;

        const optimizedName = optimizeFileName(item.file.name);
        const storagePath = existingStoragePath || `academy/videos/${Date.now()}_${optimizedName}`;
        const storageRef = ref(storage, storagePath);

        retryCountRef.current[item.id] = 0;
        updateItem(item.id, { status: 'uploading', uploadProgress: 0, error: null, storagePath });

        const uploadTask = uploadBytesResumable(storageRef, item.file, {
            contentType: item.file.type,
            customMetadata: {
                originalName: item.file.name,
                fileSize: item.file.size.toString(),
                uploadedBy: auth.currentUser.uid,
                uploadedAt: new Date().toISOString()
            }
        });

        uploadTasksRef.current[item.id] = uploadTask;
        attachUploadListeners(uploadTask, item, storagePath, optimizedName);
    }, [updateItem, attachUploadListeners]);

    const handleFilesSelected = useCallback((files) => {
        const validFiles = [];

        for (const file of files) {
            if (!SUPPORTED_FORMATS.includes(file.type)) continue;
            if (file.size > MAX_FILE_SIZE) continue;
            validFiles.push(file);
        }

        if (validFiles.length === 0) return;

        const newItems = validFiles.map(f => createVideoItem(f));
        setVideoItems(prev => [...prev, ...newItems]);

        newItems.forEach(item => startUpload(item));
    }, [startUpload]);

    const handleFileInputChange = (e) => {
        if (e.target.files?.length) {
            handleFilesSelected(Array.from(e.target.files));
            e.target.value = '';
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files?.length) {
            handleFilesSelected(Array.from(e.dataTransfer.files));
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleFormChange = (id, field, value) => {
        setVideoItems(prev => prev.map(item =>
            item.id === id
                ? { ...item, formData: { ...item.formData, [field]: value } }
                : item
        ));
    };

    const handleThumbnailChange = (id, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) return;

        const reader = new FileReader();
        reader.onload = () => {
            updateItem(id, { thumbnailFile: file, thumbnailPreview: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const handleSaveVideo = async (item) => {
        if (!auth.currentUser || item.status !== 'uploaded') return;

        updateItem(item.id, { status: 'saving', error: null });

        try {
            let thumbnailUrl = null;
            if (item.thumbnailFile) {
                const thumbPath = `academy/videos/thumbnails/${Date.now()}_${item.thumbnailFile.name}`;
                const thumbRef = ref(storage, thumbPath);
                const snap = await uploadBytes(thumbRef, item.thumbnailFile);
                thumbnailUrl = await getDownloadURL(snap.ref);
            }

            const videoData = {
                title: item.formData.title.trim(),
                description: item.formData.description.trim(),
                videoUrl: item.downloadURL,
                videoSource: 'upload',
                thumbnailUrl,
                categoryId: item.formData.categoryId,
                createdBy: auth.currentUser.email,
                createdByUserId: auth.currentUser.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                videoMetadata: item.fileMetadata
            };

            const videosRef = dbRef(database, 'Academy/videos');
            const newVideoRef = push(videosRef);
            await set(newVideoRef, videoData);

            updateItem(item.id, { status: 'saved' });

            const fns = getFunctions();
            const migrateVideoToBunny = httpsCallable(fns, 'migrateVideoToBunny');
            migrateVideoToBunny({ videoId: newVideoRef.key }).catch(() => {});

            onVideoAdded({ id: newVideoRef.key, ...videoData });

        } catch (err) {
            console.error('Error saving video:', err);
            updateItem(item.id, { status: 'uploaded', error: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' });
        }
    };

    const handleRetryUpload = (item) => {
        startUpload(item, item.storagePath);
    };

    const handleRemoveItem = (id) => {
        const task = uploadTasksRef.current[id];
        if (task) {
            task.cancel();
            delete uploadTasksRef.current[id];
        }
        delete retryCountRef.current[id];
        setVideoItems(prev => {
            const removed = prev.find(item => item.id === id);
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter(item => item.id !== id);
        });
    };

    const canSave = (item) => {
        return item.status === 'uploaded'
            && item.formData.title.trim()
            && item.formData.categoryId;
    };

    const savedCount = videoItems.filter(i => i.status === 'saved').length;
    const uploadingCount = videoItems.filter(i => i.status === 'uploading').length;

    return (
        <div className="multi-uploader">
            <div className="multi-uploader-header">
                <div className="multi-uploader-header-text">
                    <h3>Multi Video Upload</h3>
                    <p>Lade mehrere Videos gleichzeitig hoch und weise ihnen Daten zu</p>
                </div>
                <button className="multi-uploader-back-btn" onClick={onCancel}>
                    Zurück
                </button>
            </div>

            {videoItems.length > 0 && (
                <div className="multi-uploader-status-bar">
                    <span>{videoItems.length} Video{videoItems.length !== 1 ? 's' : ''}</span>
                    {uploadingCount > 0 && <span className="status-uploading">{uploadingCount} uploading...</span>}
                    {savedCount > 0 && <span className="status-saved">{savedCount} gespeichert</span>}
                </div>
            )}

            <div
                className="multi-uploader-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                <div className="dropzone-content">
                    <div className="dropzone-icon">📤</div>
                    <h4>Videos auswählen oder hierher ziehen</h4>
                    <p>MP4, WebM, OGG, AVI, MOV — max. 2GB pro Video</p>
                    <button
                        type="button"
                        className="dropzone-browse-btn"
                        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    >
                        Dateien durchsuchen
                    </button>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden-file-input"
                />
            </div>

            {videoItems.length > 0 && (
                <div className="multi-uploader-items">
                    {videoItems.map(item => (
                        <div key={item.id} className={`multi-upload-card multi-upload-card--${item.status}`}>
                            <div className="multi-upload-card-top">
                                <div className="multi-upload-preview">
                                    <video
                                        ref={el => { videoRefs.current[item.id] = el; }}
                                        src={item.previewUrl}
                                        className="multi-upload-video"
                                        muted
                                        preload="metadata"
                                    />

                                    {item.status === 'uploading' && (
                                        <div className="multi-upload-progress-overlay">
                                            <div className="multi-upload-progress-ring">
                                                <svg viewBox="0 0 36 36">
                                                    <path
                                                        className="progress-ring-bg"
                                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                    <path
                                                        className="progress-ring-fill"
                                                        strokeDasharray={`${item.uploadProgress}, 100`}
                                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                </svg>
                                                <span className="progress-ring-text">{item.uploadProgress}%</span>
                                            </div>
                                        </div>
                                    )}

                                    {item.status === 'saved' && (
                                        <div className="multi-upload-saved-overlay">
                                            <span className="saved-check">✓</span>
                                        </div>
                                    )}
                                </div>

                                <div className="multi-upload-file-info">
                                    <span className="file-name">{item.file.name}</span>
                                    <span className="file-size">{formatFileSize(item.file.size)}</span>
                                    {item.status === 'uploading' && (
                                        <div className="multi-upload-progress-bar">
                                            <div className="progress-bar-fill" style={{ width: `${item.uploadProgress}%` }} />
                                        </div>
                                    )}
                                </div>

                                {item.status !== 'saved' && item.status !== 'saving' && (
                                    <button
                                        className="multi-upload-remove-btn"
                                        onClick={() => handleRemoveItem(item.id)}
                                        title="Entfernen"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {item.error && (
                                <div className={`multi-upload-error ${item.status === 'uploading' ? 'multi-upload-error--retrying' : ''}`}>
                                    {item.error}
                                    {item.status === 'error' && (
                                        <button onClick={() => handleRetryUpload(item)} className="retry-btn">
                                            Erneut versuchen
                                        </button>
                                    )}
                                </div>
                            )}

                            {item.status !== 'saved' && (
                                <div className="multi-upload-form">
                                    <div className="multi-form-row">
                                        <div className="multi-form-field multi-form-field--title">
                                            <label>Titel *</label>
                                            <input
                                                type="text"
                                                value={item.formData.title}
                                                onChange={(e) => handleFormChange(item.id, 'title', e.target.value)}
                                                placeholder="Video Titel"
                                                maxLength={100}
                                                disabled={item.status === 'saving'}
                                            />
                                        </div>
                                        <div className="multi-form-field multi-form-field--category">
                                            <label>Kategorie *</label>
                                            <select
                                                value={item.formData.categoryId}
                                                onChange={(e) => handleFormChange(item.id, 'categoryId', e.target.value)}
                                                disabled={item.status === 'saving'}
                                            >
                                                <option value="">Auswählen...</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="multi-form-row">
                                        <div className="multi-form-field multi-form-field--desc">
                                            <label>Beschreibung</label>
                                            <textarea
                                                value={item.formData.description}
                                                onChange={(e) => handleFormChange(item.id, 'description', e.target.value)}
                                                placeholder="Optional..."
                                                rows={2}
                                                disabled={item.status === 'saving'}
                                            />
                                        </div>
                                        <div className="multi-form-field multi-form-field--thumb">
                                            <label>Thumbnail</label>
                                            <div className="multi-thumb-area">
                                                {item.thumbnailPreview ? (
                                                    <img src={item.thumbnailPreview} alt="Thumb" className="multi-thumb-preview" />
                                                ) : (
                                                    <span className="multi-thumb-placeholder">🖼️</span>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleThumbnailChange(item.id, e)}
                                                    disabled={item.status === 'saving'}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="multi-form-actions">
                                        <button
                                            className="multi-save-btn"
                                            onClick={() => handleSaveVideo(item)}
                                            disabled={!canSave(item) || item.status === 'saving'}
                                        >
                                            {item.status === 'saving' ? 'Speichert...'
                                                : item.status === 'uploading' ? `Upload ${item.uploadProgress}%...`
                                                : 'Speichern'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {item.status === 'saved' && (
                                <div className="multi-upload-saved-banner">
                                    ✓ Video gespeichert
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default MultiVideoUploader;
